import React from "react";
import styled from "styled-components";
import { Flex, Button, ButtonProps, Text, utils } from "@thenounproject/lingo-core";

import { Container, Inner } from "../Elements";

import { MarketingButtons } from "../MarketingButtons";
import MarketingText from "../MarketingText";
import Images from "../../data/images";

const DEFAULT_BACKGROUND = Images.CTA.default;

const { mq } = utils;

type Props = {
  size?: "small" | "medium" | "large";
  title?: string;
  body?: string;
  backgroundImage?: string;
  button1?: ButtonProps;
  button2?: ButtonProps;
  style?: "darkGradient" | "lightGradient";
};

const ButtonWrapper = styled(Flex).attrs({
  mt: "xl",
  alignItems: "center",
  gap: "16px",
})`
  ${mq.xs`
    width: 100%;
    text-align: center;
    flex-direction: column;
    a {
      width: 100%;
    }
  `}
`;

const CtaBlock: React.FC<Props> = ({
  title = "Build your brand on Lingo",
  body = "Fully featured 30-day trial. No credit card required.",
  backgroundImage,
  button1 = MarketingButtons.getStarted,
  button2 = MarketingButtons.bookDemo,
  style = "darkGradient",
  ...rest
}) => {
  const InnerElement = Inner;

  const backgroundColor =
    style === "darkGradient"
      ? "linear-gradient(103deg, #4349DC 18.01%, #181C85 89.28%);"
      : "linear-gradient(103deg, #FFF 19.91%, #D0D2F9 99.51%);";

  const textColor = style === "darkGradient" ? "white" : "black";

  const bg = backgroundImage || DEFAULT_BACKGROUND;
  return (
    <Container pt="xl" {...rest}>
      <InnerElement
        first
        borderRadius="8px"
        border="default"
        boxShadow="card"
        alignItems="left"
        flexDirection="column"
        justifyContent="space-between"
        px="xxl"
        py="xxl"
        backgroundImage={`url(${bg}), ${backgroundColor}`}
        backgroundSize="cover"
        backgroundPosition="right"
        variations={{ "mq.m": { backgroundImage: backgroundColor } }}>
        <Flex
          alignItems="flex-start"
          flexDirection="column"
          width="550px"
          maxWidth="75%"
          variations={{ "mq.m": { width: "100%", maxWidth: "100%" } }}>
          <Text font="marketing.h3" mb="s" color={textColor}>
            {title}
          </Text>
          {body && (
            <MarketingText font="marketing.regular" color={textColor}>
              {body}
            </MarketingText>
          )}
        </Flex>
        <ButtonWrapper>
          <Button
            buttonStyle={style === "darkGradient" ? "primary" : "outline"}
            themeOverrides={{
              outlineStyle: style === "darkGradient" ? "light" : "dark",
              primaryColor: "white",
              primaryColorDark: "gray",
              primaryColorTint: "black",
            }}
            {...button1}
            variations={{ "mq.xs": { width: "100%" } }}
          />
          <Button
            buttonStyle="outline"
            themeOverrides={{ outlineStyle: style === "lightGradient" ? "light" : "dark" }}
            {...button2}
            variations={{ "mq.xs": { width: "100%" } }}
          />
        </ButtonWrapper>
      </InnerElement>
    </Container>
  );
};

export default CtaBlock;
